import { useCallback, useMemo } from 'react';
import { PackageProviderName } from 'data/ppoints/constants';
import {
    buildCompanyLocationCountryCodesFilter,
    GET_MARKETS_QUERY,
    GetMarketsResponse,
    GetMarketsVariables,
    groupMarketsByCountryCode,
    normalizeCountryCodes,
    ShopifyMarket,
    useShopifyGraphQLSWR,
} from 'hooks/shopify';
import { GetShippingRateMappingsOptions } from '../api/shippingRateMappingApi';
import { ShippingRateMapping } from '../models/shipping-rate-mapping';
import { useShippingRateMappings } from './useShippingRateMappings';

/**
 * Shopify enforces a hard cap of 100 wildcard markets per shop, so a single page always holds
 * every market the wildcard filter can match — no pagination needed.
 */
const MAX_WILDCARD_MARKETS = 100;

export interface MappingCountryMarkets {
    countryCode: string;
    mapping: ShippingRateMapping;
    markets: ShopifyMarket[];
}

export interface UseMappingCountryMarketsReturn {
    /** Mapping countries that already have a company-location market for their country code. */
    withMarket: MappingCountryMarkets[];
    /** Mapping countries that have no such market yet. */
    withoutMarket: MappingCountryMarkets[];
    countryCodes: string[];
    isLoading: boolean;
    error?: Error;
    mutate: () => void;
}

/**
 * Splits the countries present in a provider's shipping rate mappings into the ones that already
 * have a wildcard company-location market on the shop and the ones that don't.
 */
export const useMappingCountryMarkets = (
    provider: PackageProviderName,
    mappingOptions: GetShippingRateMappingsOptions = {}
): UseMappingCountryMarketsReturn => {
    const {
        mappings,
        isLoading: isLoadingMappings,
        error: mappingsError,
        mutate: mutateMappings,
    } = useShippingRateMappings(provider, mappingOptions);

    // One mapping entry per country; later duplicates for the same code are ignored.
    //
    // Sorted by country code, because a Map iterates in insertion order and the API's own order
    // isn't stable between responses — leaving it alone makes the country cards shuffle on every
    // revalidation.
    const mappingsByCountryCode = useMemo(() => {
        const byCode = new Map<string, ShippingRateMapping>();

        mappings.forEach((mapping) => {
            const code = mapping.country?.countryCode?.trim().toUpperCase();

            if (code && !byCode.has(code)) {
                byCode.set(code, mapping);
            }
        });

        return new Map([...byCode.entries()].sort(([a], [b]) => a.localeCompare(b)));
    }, [mappings]);

    const countryCodes = useMemo(
        () => normalizeCountryCodes([...mappingsByCountryCode.keys()]),
        [mappingsByCountryCode]
    );

    const query = useMemo(() => buildCompanyLocationCountryCodesFilter(countryCodes), [countryCodes]);

    const {
        data,
        isLoading: isLoadingMarkets,
        error: marketsError,
        mutate: mutateMarkets,
    } = useShopifyGraphQLSWR<GetMarketsResponse, GetMarketsVariables>(
        query ? `markets-for-mapping-countries-${provider}` : null,
        GET_MARKETS_QUERY,
        { first: MAX_WILDCARD_MARKETS, query },
        'markets',
        // The key is derived from the mapping countries, so a save that changes which countries come
        // back changes the key. Without this the markets briefly read as empty and every country
        // jumps into `withoutMarket` until the refetch lands.
        { keepPreviousData: true }
    );

    const { withMarket, withoutMarket } = useMemo(() => {
        const marketsByCountryCode = groupMarketsByCountryCode(data?.markets?.nodes ?? []);

        return [...mappingsByCountryCode.entries()].reduce<{
            withMarket: MappingCountryMarkets[];
            withoutMarket: MappingCountryMarkets[];
        }>(
            (acc, [countryCode, mapping]) => {
                const markets = marketsByCountryCode[countryCode] ?? [];

                acc[markets.length ? 'withMarket' : 'withoutMarket'].push({ countryCode, mapping, markets });

                return acc;
            },
            { withMarket: [], withoutMarket: [] }
        );
    }, [data?.markets?.nodes, mappingsByCountryCode]);

    // Revalidates both sides, so a market created through the Shopify intent moves its country
    // from `withoutMarket` to `withMarket` without a reload.
    const refresh = useCallback(() => {
        mutateMappings();
        mutateMarkets();
    }, [mutateMappings, mutateMarkets]);

    return {
        withMarket,
        withoutMarket,
        countryCodes,
        isLoading: isLoadingMappings || isLoadingMarkets,
        error: mappingsError ?? marketsError,
        mutate: refresh,
    };
};
